import URI from 'urijs';
import _ from 'underscore';
import { appendRedirectUrl } from './url-helper';
import type {
  BaseUrlBuilderClass,
  BaseUrlBuilderInstance,
  GetUrlBuildersOptions,
  GetUrlMethod,
  UriInstance,
  UrlBuilderConfig,
  UrlBuildersResult,
  UrlQueryParams,
} from './types';

export const separator = '/';
const ccBaseShortUrl = '/cc/';
const ccBaseShortUrlWithoutCC = '/';

// eslint-disable-next-line func-names
export default function ({
  getDefaultIsShortUrl,
  aidFetcher = _.noop,
  cidFetcher = _.noop,
  uidFetcher = _.noop,
  ccBaseUrl,
}: GetUrlBuildersOptions): UrlBuildersResult {
  if (typeof ccBaseUrl === 'function') {
    // eslint-disable-next-line no-param-reassign
    ccBaseUrl = ccBaseUrl();
  }

  const ccBaseUrlWithoutCC = ccBaseUrl.indexOf('cc/') === ccBaseUrl.length - 3 ? ccBaseUrl.slice(0, -3) : ccBaseUrl;

  const createClientCenterUri = (controllerName: string, actionNames: string[], isShortUrl?: boolean) => {
    const isShortUrlValue = _.isBoolean(isShortUrl) ? isShortUrl : getDefaultIsShortUrl();
    const uri = new URI(isShortUrlValue ? ccBaseShortUrl : ccBaseUrl);

    // After uri.segment(), the path will not ends with '/'
    return uri.segment([controllerName, ...actionNames].join(separator));
  };

  const appendContextParams = (uri: UriInstance, contextQuery: UrlQueryParams) => {
    const cid = cidFetcher();
    if (cid) {
      _.defaults(contextQuery, { cid });
    }

    const aid = aidFetcher();
    if (aid) {
      _.defaults(contextQuery, { aid });
    }

    const uid = uidFetcher();
    if (uid) {
      _.defaults(contextQuery, { uid });
    }

    return uri.setSearch(contextQuery);
  };

  let BaseUrlBuilder: BaseUrlBuilderClass;

  const appendHash = (uri: UriInstance, hash: BaseUrlBuilderInstance) => {
    if (!(hash instanceof BaseUrlBuilder)) {
      throw new Error('Error: hash should be an object of UrlBuilder');
    }

    return uri.hash(hash.shortUrl);
  };

  const getCustomerUrl = ({
    controllerName,
    actionNames,
    params = {},
    hash,
    rurl,
    isShortUrl,
  }: UrlBuilderConfig) => {
    let uri = createClientCenterUri(controllerName, actionNames, isShortUrl);

    uri = appendContextParams(uri, params);

    if (rurl) {
      uri = appendRedirectUrl(uri, rurl);
    }

    if (hash) {
      uri = appendHash(uri, hash);
    }

    return uri;
  };
  /*
  * AbstractUrlBuilder - Builder of Url which can handle clientcenter and other urls
  * @function constructor(function getUrlMethod) getUrlMethod - is method to generate the url
  * @function params(object paramsPairs) paramsPairs - is a pair list of {parameter name: value}
  * @function rurl(string rurl) rurl - is a url string which indicates the redirect url
  * @getter instance - the instance of URI
  * @getter url - the string of url based on ccBaseUrl
  * @getter shortUrl - the string of short url without domain name, can be used in hash
  */
  interface AbstractUrlBuilder {
    configs: UrlBuilderConfig;
    getUrlMethod: GetUrlMethod;
  }

  class AbstractUrlBuilder {
    constructor(getUrlMethod: GetUrlMethod, configs: UrlBuilderConfig = {}) {
      this.getUrlMethod = getUrlMethod;
      this.configs = configs;
    }

    params = (paramsPairs: UrlQueryParams) => {
      _.extend(this.configs, { params: paramsPairs });
      return this;
    };

    rurl(rurl: string) {
      _.extend(this.configs, { rurl });
      return this;
    }

    get instance() {
      return this.getUrlMethod(this.configs);
    }

    get url() {
      return this.instance.toString();
    }

    get absoluteUrl() {
      return this.getUrlMethod(_.defaults({ isShortUrl: false }, this.configs)).toString();
    }

    get shortUrl() {
      return this.getUrlMethod(_.defaults({ isShortUrl: true }, this.configs)).toString();
    }
  }

  /*
  * BaseUrlBuilder - the builder of url, will append cid, aid, and uid from PageContext automatically
  * @function constructor(string controllerName) controllerName - is the controller name of the url
  * @function action(string actionNames) actionName - add an action name to the url
  * @function hash(UrlBuilder hashUrlBuilder) hashUrlBuilder - must be a BaseUrlBuilder object which indicate the hash url
  * @function withoutAid() - erase the aid from url
  */
  BaseUrlBuilder = class extends AbstractUrlBuilder {
    constructor(controllerName: string, configs: UrlBuilderConfig = {}) {
      super(getCustomerUrl, _.extend({}, { controllerName, actionNames: [] }, configs));
    }

    action(actionName: string) {
      _.extend(this.configs, { actionNames: [...this.configs.actionNames, actionName] });
      return this;
    }

    hash(hashUrlBuilder: BaseUrlBuilderInstance) {
      _.extend(this.configs, { hash: hashUrlBuilder });
      return this;
    }

    withoutAid() {
      this.configs.params = _.extend({}, this.configs.params, { aid: '' });
      return this;
    }
  };

  /**
   * Build the account settings page url
   * @constructor
   */
  class AccountSettingsUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('settings/account', configs);
    }
  }

  /**
   * Build the edit account settings page url
   * @constructor
   */
  class AccountSettingsEditUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('settings/account/edit', configs);
    }
  }

  /**
   * Build the accounts page url
   * @constructor
   */
  class AccountsUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('accounts', configs);
    }
  }

  /**
   * Build the account page url
   * @constructor
   */
  class AccountUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('account', configs);
    }
  }

  /**
   * Build the adjustments page url
   * @constructor
   */
  class AdjustmentsUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('adjustments', configs);
    }
  }

  /**
   * Build the agency page url
   * @constructor
   */
  class AgencyUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('agency', configs);
    }
  }

  /**
   * Build the billing page url
   * @constructor
   */
  class BillingUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('billing', configs);
    }
  }

  /**
   * Build the boleto page url
   * @constructor
   */
  class BoletoUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('boleto', configs);
    }
  }

  /**
   * Build the creditHold page url
   * @constructor
   */
  class CreditHoldUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('creditHold', configs);
    }
  }

  /**
   * Build the customers page url
   * @constructor
   */
  class CustomersUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('customers', configs);
    }
  }

  /**
   * Build the manager account settings page url
   * @constructor
   */
  class ManagerAccountSettingsUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('settings/manageraccount', configs);
    }
  }

  /**
   * Build the manager account settings page url
   * @constructor
   */
  class ManagerAccountSettingsEditUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('settings/manageraccount/edit', configs);
    }
  }

  /**
   * Build the managers page url
   * @constructor
   */
  class ManagersUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('managers', configs);
    }
  }

  /**
   * Build the mandateAgreementRequired page url
   * @constructor
   */
  class MandateAgreementRequiredUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('mandateAgreementRequired', configs);
    }
  }

  /**
   * Build the my settings page url
   * @constructor
   */
  class MySettingsUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('Settings/MySettings', configs);
    }
  }

  /**
   * Build the insertionOrder page url
   * @constructor
   */
  class InsertionOrderUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('insertionOrder', configs);
    }
  }

  /**
   * Build the payment page url
   * @constructor
   */
  class PaymentUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('payment', configs);
    }
  }

  /**
   * Build the paymentmethods page url
   * @constructor
   */
  class PaymentmethodsUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('paymentmethods', configs);
    }
  }

  /**
   * Build the rebill page url
   * @constructor
   */
  class RebillUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('rebill', configs);
    }
  }
  /**
 * Build the UK FCA page url
 * @constructor
 */
  class UKFCAUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('UKFCA', configs);
    }
  }

  /**
   * Build the users page url
   * @constructor
   */
  class UsersUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('users', configs);
    }
  }

  /**
   * Build the welcome page url
   * @constructor
   */
  class WelcomeUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('welcome', configs);
    }
  }

  /**
   * Build the SAPOnboarding page url
   * @constructor
   */
  class SAPOnboardingUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('SAPOnboarding', configs);
    }
  }

  /**
   * Build the MonthlyInvoice page url
   * @constructor
   */
  class MonthlyInvoiceUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('MonthlyInvoice', configs);
    }
  }

  /**
   * Build the trade screening transformation page url
   * @constructor
   */
  class TradeScreeningTransformationUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('MasterData', configs);
    }
  }

  /**
   * Build the Notification page url
   * @constructor
   */
  class NotificationUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('Notifications', configs);
    }
  }

  class BillingApiUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('api/billing', configs);
    }
  }

  class ApiAccountUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('api/account', configs);
    }
  }

  class ApiCashbackapprovalUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('api/cashbackapproval', configs);
    }
  }
  class ApiFileUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('api/file', configs);
    }
  }

  class ApiPaymentInstrumentUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('api/paymentInstrument', configs);
    }
  }

  class FraudAppealAIVUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('FraudAppeal/AccountIdentityVerification', configs);
    }
  }

  class FraudAppealAIVWelcomeUrlBuilder extends BaseUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super('FraudAppeal/AccountIdentityVerificationWelcome', configs);
    }
  }

  const getLogoutUrl = ({
    params = {},
    rurl,
    isShortUrl,
  }: UrlBuilderConfig) => {
    let uri = new URI(isShortUrl ? ccBaseShortUrlWithoutCC : ccBaseUrlWithoutCC);

    // After uri.segment(), the path will not ends with '/'
    uri = uri.segment('logout');

    uri = uri.setSearch(params);

    if (rurl) {
      uri = appendRedirectUrl(uri, rurl);
    }

    return uri;
  };

  /**
   * Build the logout url
   * @constructor
   */
  class LogoutUrlBuilder extends AbstractUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super(getLogoutUrl, configs);
    }
  }

  const getLoginBlockedUrl = ({
    params = {},
    rurl,
    isShortUrl,
  }: UrlBuilderConfig) => {
    let uri = new URI(isShortUrl ? ccBaseShortUrlWithoutCC : ccBaseUrlWithoutCC);

    // After uri.segment(), the path will not ends with '/'
    uri = uri.segment('Login/Blocked');

    uri = uri.setSearch(params);

    if (rurl) {
      uri = appendRedirectUrl(uri, rurl);
    }

    return uri;
  };

  /**
   * Build the Login blocked url
   * @constructor
   */
  class LoginBlockedUrlBuilder extends AbstractUrlBuilder {
    constructor(configs?: UrlBuilderConfig) {
      super(getLoginBlockedUrl, configs);
    }
  }

  return ({
    BaseUrlBuilder,
    AccountSettingsUrlBuilder,
    AccountSettingsEditUrlBuilder,
    AccountsUrlBuilder,
    AccountUrlBuilder,
    AdjustmentsUrlBuilder,
    AgencyUrlBuilder,
    ApiAccountUrlBuilder,
    ApiCashbackapprovalUrlBuilder,
    ApiFileUrlBuilder,
    ApiPaymentInstrumentUrlBuilder,
    BillingApiUrlBuilder,
    BillingUrlBuilder,
    BoletoUrlBuilder,
    CreditHoldUrlBuilder,
    CustomersUrlBuilder,
    FraudAppealAIVUrlBuilder,
    FraudAppealAIVWelcomeUrlBuilder,
    InsertionOrderUrlBuilder,
    LoginBlockedUrlBuilder,
    LogoutUrlBuilder,
    ManagerAccountSettingsUrlBuilder,
    ManagerAccountSettingsEditUrlBuilder,
    ManagersUrlBuilder,
    MandateAgreementRequiredUrlBuilder,
    MonthlyInvoiceUrlBuilder,
    MySettingsUrlBuilder,
    NotificationUrlBuilder,
    PaymentUrlBuilder,
    PaymentmethodsUrlBuilder,
    RebillUrlBuilder,
    SAPOnboardingUrlBuilder,
    TradeScreeningTransformationUrlBuilder,
    UKFCAUrlBuilder,
    UsersUrlBuilder,
    WelcomeUrlBuilder,
  });
}
